import {
  NOTIFICATIONS_METAFIELDS_NAMESPACE,
  NOTIFICATION_METAFIELD_KEYS
} from '@portal/api/constants';
import { useSessionValue } from '@portal/appLayer/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRoutes } from 'constants/routes';
import { useCallback } from 'react';
import { StoreCode } from 'schema/storeCode';
import { fetcher } from 'shared/utils/fetcher';
import { useStoreValue } from 'state/hooks/useStore';
import { getQueryKeyForGetCustomerMetafields } from '../getCustomerMetafields/getCustomerMetafields';

interface CustomerMetafield {
  namespace: string;
  key: string;
  value: string;
  type?: string;
}

async function getCustomerMetafields(store?: StoreCode, customerId?: string) {
  if (!store || !customerId) return undefined;
  const { data } = await fetcher.get<CustomerMetafield[]>(
    apiRoutes.members.getCustomerMetafields({ store }, { customerId })
  );
  return data;
}

async function updateCustomerMetafield(
  notificationKey: keyof typeof NOTIFICATION_METAFIELD_KEYS,
  store?: StoreCode,
  customerId?: string
) {
  if (!store || !customerId) return undefined;
  const { data } = await fetcher.post(
    apiRoutes.members.setCustomerNotificationMetafields({ store }),
    {
      customerId,
      notificationKey
    }
  );
  return data;
}

export function useNotificationState(notificationKey: keyof typeof NOTIFICATION_METAFIELD_KEYS) {
  const store = useStoreValue();
  const queryClient = useQueryClient();
  const session = useSessionValue();
  const customerId = session?.customer.id;
  const queryKey = getQueryKeyForGetCustomerMetafields(store, customerId);

  const { data: customerMetafields, isPending: isLoadingMetafields } = useQuery({
    queryKey: getQueryKeyForGetCustomerMetafields(store, customerId),
    queryFn: () => getCustomerMetafields(store, String(customerId)),
    enabled: Boolean(store && customerId)
  });

  const { isPending: dismissIsPending, mutate: dismissMutate } = useMutation({
    mutationFn: () => updateCustomerMetafield(notificationKey, store, String(customerId)),
    onMutate: async () => {
      // Cancel any outgoing refetches to avoid them overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey
      });

      // Get the current metafields
      const previousMetafields = queryClient.getQueryData<CustomerMetafield[]>(queryKey);

      // Create the new metafield
      const newMetafield = {
        namespace: NOTIFICATIONS_METAFIELDS_NAMESPACE,
        key: NOTIFICATION_METAFIELD_KEYS[notificationKey],
        value: 'true'
      };

      // Optimistically update by either updating existing metafield or adding new one
      queryClient.setQueryData(queryKey, (old: CustomerMetafield[] = []) => {
        const existingIndex = old.findIndex(
          (metafield) =>
            metafield.namespace === NOTIFICATIONS_METAFIELDS_NAMESPACE &&
            metafield.key === NOTIFICATION_METAFIELD_KEYS[notificationKey]
        );

        if (existingIndex !== -1) {
          // Update existing metafield
          const updated = [...old];
          updated[existingIndex] = {
            ...updated[existingIndex],
            value: 'true'
          };
          return updated;
        }

        // Add new metafield
        return [...old, newMetafield];
      });

      return { previousMetafields };
    },
    onError: (error, _variables, context) => {
      // On error, roll back to the previous value
      if (context?.previousMetafields) {
        queryClient.setQueryData(queryKey, context.previousMetafields);
      }
      // eslint-disable-next-line no-console
      console.error('Failed to update notification state:', error);
    },
    onSettled: () => {
      // Refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const isDismissed =
    customerMetafields?.some(
      (metafield) =>
        metafield.namespace === NOTIFICATIONS_METAFIELDS_NAMESPACE &&
        metafield.key === NOTIFICATION_METAFIELD_KEYS[notificationKey] &&
        metafield.value === 'true'
    ) ?? false;

  // Don't show as open while loading or if dismissed
  const isOpen = !isLoadingMetafields && !isDismissed;

  const handleClose = useCallback(() => {
    if (!dismissIsPending) {
      dismissMutate();
    }
  }, [dismissIsPending, dismissMutate]);

  return {
    isOpen,
    handleClose,
    isLoading: isLoadingMetafields || dismissIsPending
  };
}
